import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Footer } from "../../layout/footer";
import { reportCRUD } from "../../actions/report";
import { boundaryCRUD } from "../../actions/boundary";
import { Loading } from "../constant";

const nf = (n) => (n ?? 0).toLocaleString();

// Choropleth colour ramp by population (light -> dark green).
const choroplethColor = (value, max) => {
  if (!value) return "#e5e7eb";
  const r = max > 0 ? value / max : 0;
  if (r > 0.66) return "#0a7d5a";
  if (r > 0.33) return "#25a87a";
  if (r > 0.1) return "#6ec4a3";
  return "#c3e8d6";
};

// Choropleth map: children polygons shaded by population, click to drill down.
const ChoroplethMap = ({ boundaryId, childTypeId, children, onDrill }) => {
  const [geo, setGeo] = useState(null);
  const popById = {};
  children.forEach((c) => { popById[c.id] = c.population || 0; });
  const maxPop = Math.max(1, ...children.map((c) => c.population || 0));

  useEffect(() => {
    setGeo(null);
    if (!childTypeId) return;
    boundaryCRUD
      .loadBoundaryGeoJson(childTypeId, boundaryId || "")
      .then((r) => setGeo(r.data))
      .catch(() => setGeo(null));
  }, [boundaryId, childTypeId]);

  if (!geo || !geo.features || geo.features.length === 0) {
    return <div className="text-muted text-center py-5">No map geometry for this level.</div>;
  }

  return (
    <MapContainer center={[-6.0, 39.35]} zoom={9} style={{ height: 360, borderRadius: 6 }} scrollWheelZoom={false}>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <GeoJSON
        key={boundaryId || "root"}
        data={geo}
        style={(f) => ({
          fillColor: choroplethColor(popById[f.properties.id] || 0, maxPop),
          weight: 1,
          color: "#ffffff",
          fillOpacity: 0.75,
        })}
        onEachFeature={(feature, layer) => {
          const pop = popById[feature.properties.id] || 0;
          layer.bindTooltip(`${feature.properties.boundary_name}: ${pop.toLocaleString()} people`);
          layer.on("click", () => onDrill && onDrill({ id: feature.properties.id, name: feature.properties.boundary_name }));
        }}
      />
    </MapContainer>
  );
};

// One statistic tile (population / households / buildings).
const StatTile = ({ label, value, icon, tone }) => (
  <div className="col">
    <div className={`card border-0 shadow-sm h-100 bg-soft-${tone}`}>
      <div className="card-body d-flex align-items-center">
        <div className={`avatar-sm rounded bg-${tone} d-flex align-items-center justify-content-center me-3`}>
          <i className={`${icon} text-white fs-20`}></i>
        </div>
        <div>
          <p className="text-muted mb-1 small">{label}</p>
          <h4 className="mb-0">{nf(value)}</h4>
        </div>
      </div>
    </div>
  </div>
);

export const StatisticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Breadcrumb trail for drill-down: [{id, name}]. Root (Zanzibar) has id=null.
  const [trail, setTrail] = useState([{ id: null, name: "Zanzibar" }]);

  const load = (boundaryId) => {
    setLoading(true);
    reportCRUD
      .loadStatisticsDashboard(boundaryId)
      .then((resp) => setData(resp.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(null);
  }, []);

  const drillInto = (child) => {
    setTrail((t) => [...t, { id: child.id, name: child.name }]);
    load(child.id);
  };

  const goTo = (index) => {
    const target = trail[index];
    setTrail((t) => t.slice(0, index + 1));
    load(target.id);
  };

  const t = data?.totals || {};
  const children = data?.children || [];

  // Bar chart: population + buildings per child (top 15 to stay readable).
  const barChildren = [...children]
    .sort((a, b) => (b.population || 0) - (a.population || 0))
    .slice(0, 15);
  const barSeries = [
    { name: "Population", data: barChildren.map((c) => c.population || 0) },
    { name: "Buildings", data: barChildren.map((c) => c.buildings || 0) },
  ];
  const barOptions = {
    chart: { type: "bar", toolbar: { show: false }, stacked: false },
    plotOptions: { bar: { columnWidth: "60%", borderRadius: 3 } },
    colors: ["#0ab39c", "#4b8df8"],
    dataLabels: { enabled: false },
    xaxis: { categories: barChildren.map((c) => c.name), labels: { rotate: -45, style: { fontSize: "11px" } } },
    legend: { position: "top" },
    grid: { borderColor: "#f1f1f1" },
  };

  // Population pyramid: male (left, negative) vs female (right, positive) by age.
  const pyr = data?.age_pyramid || { labels: [], male: [], female: [] };
  const pyramidSeries = [
    { name: "Male", data: (pyr.male || []).map((v) => -v) },
    { name: "Female", data: pyr.female || [] },
  ];
  const pyramidOptions = {
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, barHeight: "80%" } },
    colors: ["#4b8df8", "#f06548"],
    dataLabels: { enabled: false },
    xaxis: {
      categories: pyr.labels || [],
      labels: { formatter: (v) => Math.abs(Math.round(v)).toString() },
    },
    yaxis: { title: { text: "Age group" } },
    tooltip: { y: { formatter: (v) => Math.abs(v).toLocaleString() } },
    legend: { position: "top" },
  };
  const hasPyramid = (pyr.male || []).some((v) => v > 0) || (pyr.female || []).some((v) => v > 0);

  // Donut: gender split for this level.
  const genderSeries = [t.male || 0, t.female || 0];
  const genderOptions = {
    chart: { type: "donut" },
    labels: ["Male", "Female"],
    colors: ["#4b8df8", "#f06548"],
    legend: { position: "bottom" },
    dataLabels: { enabled: true, formatter: (v) => `${Math.round(v)}%` },
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* Header + breadcrumb */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0">
                  <i className="ri-bar-chart-2-line align-bottom me-1 text-primary"></i>
                  Statistics Dashboard
                </h4>
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    {trail.map((crumb, i) => (
                      <li
                        key={i}
                        className={`breadcrumb-item ${i === trail.length - 1 ? "active" : ""}`}
                      >
                        {i === trail.length - 1 ? (
                          crumb.name
                        ) : (
                          <a href="#" onClick={(e) => { e.preventDefault(); goTo(i); }}>
                            {crumb.name}
                          </a>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {loading && <div className="row"><Loading /></div>}

          {!loading && data && (
            <>
              {/* Level title */}
              <div className="row mb-2">
                <div className="col-12">
                  <h5 className="text-primary mb-0">
                    {data.name}
                    <span className="badge bg-soft-primary text-primary ms-2">{data.level}</span>
                  </h5>
                </div>
              </div>

              {/* Totals for this level */}
              <div className="row row-cols-1 row-cols-md-4 g-3 mb-2">
                <StatTile label="Population" value={t.population} icon="ri-group-line" tone="primary" />
                <StatTile label="Households" value={t.households} icon="ri-home-4-line" tone="success" />
                <StatTile label="Buildings" value={t.buildings} icon="ri-building-2-line" tone="info" />
                <div className="col">
                  <div className="card border-0 shadow-sm h-100 bg-soft-warning">
                    <div className="card-body">
                      <p className="text-muted mb-1 small">Gender</p>
                      <div className="d-flex justify-content-between">
                        <span><i className="ri-men-line text-primary"></i> {nf(t.male)}</span>
                        <span><i className="ri-women-line text-danger"></i> {nf(t.female)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts: population/buildings per child + gender split */}
              {children.length > 0 && (
                <div className="row g-3 mt-1">
                  <div className="col-lg-8">
                    <div className="card h-100">
                      <div className="card-header">
                        <h6 className="mb-0">Population &amp; Buildings by {data.child_level}</h6>
                      </div>
                      <div className="card-body">
                        <ReactApexChart options={barOptions} series={barSeries} type="bar" height={320} />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="card h-100">
                      <div className="card-header">
                        <h6 className="mb-0">Gender ({data.name})</h6>
                      </div>
                      <div className="card-body d-flex align-items-center justify-content-center">
                        {(t.male || t.female) ? (
                          <ReactApexChart options={genderOptions} series={genderSeries} type="donut" height={300} />
                        ) : (
                          <span className="text-muted">No gender data</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Choropleth map of children shaded by population */}
              {children.length > 0 && children[0].type_id && (
                <div className="card mt-3">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Map — Population by {data.child_level}</h6>
                    <span className="text-muted small">Darker = more people · click an area to drill down</span>
                  </div>
                  <div className="card-body">
                    <ChoroplethMap
                      boundaryId={data.boundary_id}
                      childTypeId={children[0].type_id}
                      children={children}
                      onDrill={drillInto}
                    />
                  </div>
                </div>
              )}

              {/* Population pyramid (age x gender) */}
              {hasPyramid && (
                <div className="card mt-3">
                  <div className="card-header">
                    <h6 className="mb-0">Population Pyramid ({data.name})</h6>
                  </div>
                  <div className="card-body">
                    <ReactApexChart options={pyramidOptions} series={pyramidSeries} type="bar" height={340} />
                  </div>
                </div>
              )}

              {/* Children breakdown (drill-down cards) */}
              {data.children && data.children.length > 0 && (
                <div className="card mt-2">
                  <div className="card-header">
                    <h6 className="mb-0">
                      By {data.child_level}
                      <span className="text-muted small ms-2">({data.children.length}) — click to drill down</span>
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead>
                          <tr>
                            <th>{data.child_level}</th>
                            <th className="text-end">Population</th>
                            <th className="text-end">Male</th>
                            <th className="text-end">Female</th>
                            <th className="text-end">Households</th>
                            <th className="text-end">Buildings</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.children.map((c) => (
                            <tr
                              key={c.id}
                              style={{ cursor: "pointer" }}
                              onClick={() => drillInto(c)}
                            >
                              <td className="fw-semibold">{c.name}</td>
                              <td className="text-end">{nf(c.population)}</td>
                              <td className="text-end">{nf(c.male)}</td>
                              <td className="text-end">{nf(c.female)}</td>
                              <td className="text-end">{nf(c.households)}</td>
                              <td className="text-end">{nf(c.buildings)}</td>
                              <td className="text-end">
                                <i className="ri-arrow-right-s-line text-muted"></i>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {(!data.children || data.children.length === 0) && (
                <div className="alert alert-info mt-2">
                  <i className="ri-information-line me-1"></i>
                  This is the lowest level — no further breakdown.
                </div>
              )}
            </>
          )}

          {!loading && !data && (
            <div className="alert alert-warning">Failed to load statistics.</div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};
